import { DevicePlatform } from '../../enums';
import {
    IVcfHealthReportData,
    IVcfMaintenanceStatusResponse,
    IVcfWarningLight,
    VcfHealthClient
} from "./VcfHealthClient";
import { IHealthReportSettings, VcfHealthSettingsConverter } from "./VcfHealthSettingsConverter";

export interface IMaintenanceStatus {
    readonly inspectionDueDays?: number;
    readonly inspectionDueKm?: number;
    readonly oilServiceDueDays?: number;
    readonly oilServiceDueKm?: number;
}

export enum WarningLightsCategory {
    ASSISTANCE = 'ASSISTANCE',
    BRAKE = 'BRAKE',
    COMFORT = 'COMFORT',
    ENGINE = 'ENGINE',
    LIGHTING = 'LIGHTING',
    OTHER = 'OTHER',
    TIRE = 'TIRE'
}

export interface IWarningLight {
    readonly text?: string;
    readonly priority?: string;
    readonly icon?: string;
}

export interface IGroupedWarningLights {
    readonly category: WarningLightsCategory;
    readonly warningLights?: IWarningLight[];
}

export interface IHealthReport {
    readonly lastUpdate?: string;
    readonly mileageKm?: number | null;
    readonly groupedWarningLights?: IGroupedWarningLights[];
}

export interface IHealthReportArchive {
    readonly id?: number;
    readonly lastUpdate?: string;
    readonly mileageKm?: number | null;
}

export class VcfHealthService {
    constructor(private readonly vcfHealthClient: VcfHealthClient) {
    }

    public async getMaintenanceStatus(vin: string, devicePlatform: DevicePlatform): Promise<IMaintenanceStatus> {
        let maintenanceStatusResponse: IVcfMaintenanceStatusResponse;
        maintenanceStatusResponse = await this.vcfHealthClient.getMaintenanceStatus(vin, this.isWcar(devicePlatform));
        const data = maintenanceStatusResponse.data;
        return {
            inspectionDueDays: data?.inspectionDue_days ?? undefined,
            inspectionDueKm: data?.inspectionDue_km ?? undefined,
            oilServiceDueDays: data?.oilServiceDue_days ?? undefined,
            oilServiceDueKm: data?.oilServiceDue_km ?? undefined
        };
    }

    public async getHealthReportArchives(vin: string, devicePlatform: DevicePlatform): Promise<IHealthReportArchive[]> {
        const healthReportsResponse = await this.vcfHealthClient.getHealthReports(vin, this.isWcar(devicePlatform));
        if (healthReportsResponse?.data) {
            return this.sortHealthReportsByTimestampDesc(healthReportsResponse.data).map(value => {
                    return {
                        id: value.id,
                        mileageKm: value.mileage_km,
                        lastUpdate: value.carCapturedTimestamp
                    }
                }
            );
        }
        return [];
    }

    public async deleteHealthReport(vin: string, devicePlatform: DevicePlatform, id: number): Promise<void> {
        await this.vcfHealthClient.deleteHealthReport(vin, this.isWcar(devicePlatform), id);
    }

    public async getLatestHealthReport(vin: string, devicePlatform: DevicePlatform): Promise<IHealthReport> {
        const latestHealthReportResponse = await this.vcfHealthClient.getLatestHealthReport(vin, this.isWcar(devicePlatform));
        let warningLights = latestHealthReportResponse?.data.warningLights ?? [];

        if (devicePlatform === DevicePlatform.WCAR) {
            warningLights = warningLights.filter((warningLight) => warningLight.customerRelevance ?? true) ?? [];
        }
        const groupedWarningLights = this.groupWarningLightsByCategory(warningLights);
        return {
            lastUpdate: latestHealthReportResponse?.data?.carCapturedTimestamp,
            mileageKm: latestHealthReportResponse?.data?.mileage_km,
            groupedWarningLights
        };
    }

    public async getHealthReportById(vin: string, devicePlatform: DevicePlatform, id: number): Promise<IHealthReport> {
        const healthReportResponse = await this.vcfHealthClient.getHealthReportById(vin, this.isWcar(devicePlatform), id);
        const groupedWarningLights = this.groupWarningLightsByCategory(healthReportResponse?.data.warningLights ?? []);
        return {
            lastUpdate: healthReportResponse?.data?.carCapturedTimestamp,
            mileageKm: healthReportResponse?.data?.mileage_km,
            groupedWarningLights
        };
    }

    public async getHealthReportSettings(vin: string, devicePlatform: DevicePlatform): Promise<IHealthReportSettings> {
        const healthReportSettingsResponse = await this.vcfHealthClient.getHealthReportSettings(vin, this.isWcar(devicePlatform));
        return VcfHealthSettingsConverter.convertVcfResponseToHealthReportSettings(healthReportSettingsResponse);
    }

    public async saveHealthReportSettings(vin: string, devicePlatform: DevicePlatform, healthReportSettings: IHealthReportSettings) {
        await this.vcfHealthClient.saveHealthReportSettings(vin, this.isWcar(devicePlatform),
            VcfHealthSettingsConverter.convertHealthReportSettingsToVcfData(healthReportSettings));
    }

    public async triggerWarningLightsUpdate(vin: string, devicePlatform: DevicePlatform): Promise<void> {
        await this.vcfHealthClient.triggerWarningLightsUpdate(vin, this.isWcar(devicePlatform));
    }

    private isWcar(devicePlatform: DevicePlatform) {
        return devicePlatform === DevicePlatform.WCAR
    }

    private sortHealthReportsByTimestampDesc(healthReports: IVcfHealthReportData[]): IVcfHealthReportData[] {
        return healthReports.sort((firstReport: IVcfHealthReportData, secondReport: IVcfHealthReportData) => {
            const timestampFirstReport = new Date(firstReport.carCapturedTimestamp!).getTime();
            const timestampSecondReport = new Date(secondReport.carCapturedTimestamp!).getTime();
            return timestampFirstReport > timestampSecondReport ? -1 : 1;
        })
    }

    private groupWarningLightsByCategory(warningLights: IVcfWarningLight[]): IGroupedWarningLights[] {
        const result: IGroupedWarningLights[] = [];

        warningLights?.forEach((warningLight: IVcfWarningLight) => {
            const category = warningLight.category as WarningLightsCategory;
            if (category) {
                const transformedWarningLight = {
                    text: warningLight.text,
                    icon: warningLight.icon ?? undefined,
                    priority: warningLight.priority
                };
                const categoryInResult = result.find(groupedWarningLight => groupedWarningLight.category === category);

                if (categoryInResult && categoryInResult.warningLights) {
                    categoryInResult.warningLights.push(transformedWarningLight);
                } else {
                    result.push({category, warningLights: [transformedWarningLight]});
                }
            }
        });

        return result;
    }
}
